/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { TMesurabilityProject } from '@features/offsets/types';
import Decimal from 'decimal.js';
import { useMemo } from 'react';

/* -------------------------------------------------------------------------- */
/*                                  Constants                                  */
/* -------------------------------------------------------------------------- */
const VAT_RATE = 1.2;

/* -------------------------------------------------------------------------- */
/*                                  Hook                                       */
/* -------------------------------------------------------------------------- */
export const useOffsetSummary = (
	items: TMesurabilityProject[] = []
) => {
	/* ----------------------------- Helpers ---------------------------------- */
	const safeDecimal = (value: any) => {
		try {
			return new Decimal(value || 0);
		} catch {
			return new Decimal(0);
		}
	};

	const sumDecimal = (key: keyof TMesurabilityProject) =>
		items.reduce(
			(acc, item) => acc.plus(safeDecimal(item[key])),
			new Decimal(0)
		);

	/* ----------------------------- Memoized Calculation -------------------- */
	return useMemo(() => {
		const totalQuantity = sumDecimal('quantitytco2eq');
		const totalExclVAT = items.reduce((acc, item) => {
			const price = safeDecimal(
				item.sellPrice_europertco2eq
			);
			const qty = safeDecimal(item.quantitytco2eq);
			return acc.plus(price.mul(qty));
		}, new Decimal(0));

		const averagePrice = totalQuantity.gt(0)
			? totalExclVAT.div(totalQuantity)
			: new Decimal(0);

		const totalInclVAT = totalExclVAT.mul(VAT_RATE);

		/* ----------------------------- Return Plain Numbers ------------------- */
		return {
			totalQuantity: totalQuantity.toNumber(),
			totalExclVAT: totalExclVAT.toNumber(),
			averagePrice: averagePrice.toNumber(),
			totalInclVAT: totalInclVAT.toNumber(),
		};
	}, [items]);
};
